import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { EmphasizedNumber, Icons, InformationModal } from 'utoniq-ui'

import {
  Maybe,
  PurchasablePrepaidPoint,
  PurchasablePrepaidPointConnectionFragment,
  PurchasablePrepaidPointMetadata,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { BrowserLink } from '@/components/lib/BrowserLink'
import { ScrollFetcher } from '@/components/organisms/functionals'
import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '@/components/organisms/functionals/PaymentErrorModal'
import { constants } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Organism, Template } from '@/core/architecture'
import { App } from '@/core/native'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type PrepaidPointPlan = Pick<
  PurchasablePrepaidPoint,
  'id' | 'points' | 'price' | 'description' | 'prepaidPointId'
> & {
  metadata: Pick<PurchasablePrepaidPointMetadata, 'id' | 'platform'>
}
export type State = {
  loading: boolean
  hasNextPage: boolean
  point: Maybe<number>
  purchasablePrepaidPoints: Maybe<PurchasablePrepaidPointConnectionFragment>
  chargeLoading: boolean
  isCardRegistered: boolean
}

export type Action = {
  onClickCharge: (plan: PrepaidPointPlan) => Promise<void>
  onFetchMore: () => Promise<void>
}

export const PrepaidPointChargeTemplate: Template<State, Action> = ({
  state,
  onClickCharge,
  onFetchMore,
}) => {
  const { t } = useTranslation('prepaidPointCharge')
  const { routerNames, routerPages } = useRouter()
  const { showError } = useMessage()
  const { analytics } = useAnalytics()
  const [purchasedPlan, setPurchasedPlan] = useState<PrepaidPointPlan | null>(
    null
  )
  const [openCompleteModal, setOpenCompleteModal] = useState(false)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  // Webでクレジットカードを登録していない場合に案内を表示するためのフラグ
  const [cardModalOpen, setCardModalOpen] = useState(false)
  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)
  const isWeb = App.getPlatform() === 'web'

  const handlePointClick = async (plan: PrepaidPointPlan) => {
    if (isWeb && !state.isCardRegistered) {
      setCardModalOpen(true)
      return
    }
    setPurchasedPlan(plan)
    setOpenConfirmModal(true)
  }

  const handlePointCharge = async () => {
    if (!purchasedPlan) {
      setOpenConfirmModal(false)
      return
    }
    onClickCharge(purchasedPlan)
      .then(() => {
        setOpenConfirmModal(false)
        setOpenCompleteModal(true)
      })
      .catch(e => {
        switch (e.graphQLErrors?.[0]?.extensions?.errorCode) {
          /**
           * @description
           * 3Dセキュア認証が必要な場合
           */
          case constants.errorCodes.REQUIRE_3D_SECURE:
            setOpenConfirmModal(false)
            setOpenRequire3DSecureModal(true)
            break
          /**
           * @desciption
           * カードが有効期限切れの場合
           */
          case constants.errorCodes.EXPIRED_CARD:
            setOpenConfirmModal(false)
            setOpenExpiredCardModal(true)
            break
          /**
           * @description
           * 何かしらの決済エラーの場合
           */
          case constants.errorCodes.PAYMENT_ERROR:
            setOpenConfirmModal(false)
            setOpenPaymentErrorModal(true)
            break
          default:
            setOpenConfirmModal(false)
            showError(e)
            break
        }
      })
  }

  return (
    <BaseLayout
      margin
      header={{
        title: t('title'),
        back: true,
        defaultBackPage: routerPages.Wallet,
      }}
    >
      <Head title={routerNames.PrepaidPointCharge} />
      {state.loading && state.point === null ? (
        <>
          <PointDisplaySkeletonCell />
        </>
      ) : (
        <PointDisplayCell point={state.point} />
      )}

      <Stack spacing={4} sx={{ pt: 4 }}>
        <ScrollFetcher
          componentProps={{ sx: { pt: 2, pb: 2 }, spacing: 4 }}
          nodes={state.purchasablePrepaidPoints?.edges?.map(
            ({ node }, index) => (
              <PointChargeCardCell
                key={index}
                plan={node}
                onClick={() => {
                  analytics?.track('click_point', {
                    productId: node.metadata.id,
                    prepaidPointId: node.prepaidPointId,
                    name: 'prepaidPointCharge',
                  })
                  return handlePointClick(node)
                }}
              />
            )
          )}
          onFetchMore={onFetchMore}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          emptyMessage={t('emptyPurchasablePrepaidPoints')}
          loadingComponent={
            <Stack spacing={4} sx={{ pb: 4 }}>
              {Array.from(Array(6).keys()).map(key => (
                <PointChargeCardSkeletonCell key={key} />
              ))}
            </Stack>
          }
        />
      </Stack>

      <TextLinkCell />

      <CompletePointChargeModal
        open={openCompleteModal}
        onClose={() => setOpenCompleteModal(false)}
        point={purchasedPlan?.points}
      />

      <ConfirmPointChargeModal
        loading={state.chargeLoading}
        onConfirm={handlePointCharge}
        open={openConfirmModal}
        onCancel={() => {
          if (purchasedPlan) {
            analytics?.track('click_cancel', {
              productId: purchasedPlan.metadata.id,
              prepaidPointId: purchasedPlan.prepaidPointId,
              type: 'modal',
              name: 'purchasePoint',
            })
          }
        }}
        onClose={() => setOpenConfirmModal(false)}
        point={purchasedPlan?.points}
        price={purchasedPlan?.price}
      />

      <CardRegisterModal
        open={cardModalOpen}
        onClose={() => setCardModalOpen(false)}
      />

      <ExpiredCardModal
        open={openExpiredCardModal}
        onClose={() => setOpenExpiredCardModal(false)}
      />

      <PaymentErrorModal
        open={openPaymentErrorModal}
        onClose={() => setOpenPaymentErrorModal(false)}
      />

      <Require3DSecureModal
        open={openRequire3DSecureModal}
        onClose={() => setOpenRequire3DSecureModal(false)}
      />
    </BaseLayout>
  )
}

type PointDisplayCell = {
  point: State['point']
}
export const PointDisplayCell: Cell<PointDisplayCell> = ({ point }) => {
  const { t } = useTranslation('prepaidPointCharge')

  return (
    <CellFrame sx={{ py: 2, textAlign: 'center' }}>
      <Typography variant="h6" color="text.secondary">
        {t('pointBalance')}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Icons.Point fontSize="large" />
        <EmphasizedNumber variant="h1" sx={{ ml: 2 }}>
          {point}
        </EmphasizedNumber>
      </Box>
    </CellFrame>
  )
}

const PointDisplaySkeletonCell: Cell = () => {
  return (
    <Stack spacing={2} alignItems="center" flex={1} sx={{ pt: 2, pb: 3 }}>
      <Skeleton variant="rectangular" width={96} height={16} />
      <Skeleton variant="rectangular" width={96} height={36} />
    </Stack>
  )
}

type PointChargeCardCell = {
  plan: PrepaidPointPlan
  onClick: () => void
}
export const PointChargeCardCell: Cell<PointChargeCardCell> = ({
  plan,
  onClick,
}) => {
  const { points, price, description } = plan || {}

  return (
    <CellFrame>
      <Card>
        <CardActionArea onClick={onClick}>
          <Stack direction="row" alignItems="center" sx={{ px: 5, py: 4 }}>
            <Icons.Point fontSize="medium" />
            <EmphasizedNumber variant="h5" sx={{ ml: 2 }}>
              {points}
            </EmphasizedNumber>
            <EmphasizedNumber
              variant="overline"
              color="primary.main"
              sx={{ ml: 2 }}
            >
              {description}
            </EmphasizedNumber>
            <EmphasizedNumber variant="h5" sx={{ ml: 'auto' }}>
              ¥{price}
            </EmphasizedNumber>
          </Stack>
        </CardActionArea>
      </Card>
    </CellFrame>
  )
}

export const TextLinkCell: Cell = () => {
  const { t } = useTranslation('prepaidPointCharge')
  return (
    <CellFrame sx={{ pb: 4 }}>
      <Card elevation={0}>
        <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
          <Stack spacing={2} alignItems="center" justifyContent="center">
            <Typography variant="caption" color="text.secondary">
              {t('cancelPurchaseNote')}
            </Typography>
            <Stack direction="row" spacing={5}>
              <BrowserLink
                variant="subtitle1"
                href={getHelpUrl('wageSettlementMethod')}
              >
                {t('wageSettlementMethod')}
              </BrowserLink>
              <BrowserLink
                variant="subtitle1"
                href={getHelpUrl('specifiedCommercialTransactionLaw')}
              >
                {t('specifiedCommercialTransactionLaw')}
              </BrowserLink>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </CellFrame>
  )
}

type ConfirmPointChargeModalProps = {
  loading: boolean
  open: boolean
  onCancel: () => void
  onClose: () => void
  point?: number
  price?: number
  onConfirm: () => Promise<void>
}
export const ConfirmPointChargeModal: Organism<ConfirmPointChargeModalProps> =
  ({ loading, open, onClose, onCancel, point, price, onConfirm }) => {
    const { t } = useTranslation('prepaidPointCharge')
    return (
      <InformationModal
        open={open}
        onClose={() => {
          onCancel()
          onClose()
        }}
        title={t('confirmCharge')}
        content={t('confirmChargeDescription', { point, price })}
        buttons={
          <>
            <Button
              sx={{ minWidth: 212 }}
              variant="contained"
              color="primary"
              size="large"
              onClick={onConfirm}
              loading={loading}
            >
              {t('toBuy')}
            </Button>
            <Button
              sx={{ minWidth: 212 }}
              variant="text"
              color="primary"
              onClick={onClose}
              disabled={loading}
            >
              {t('cancel')}
            </Button>
          </>
        }
      />
    )
  }

type CompletePointChargeModalProps = {
  open: boolean
  onClose: () => void
  point?: number
}
export const CompletePointChargeModal: Organism<CompletePointChargeModalProps> =
  ({ open, point, onClose }) => {
    const { t } = useTranslation('prepaidPointCharge')
    return (
      <InformationModal
        open={open}
        onClose={onClose}
        title={t('completeCharge')}
        content={t('completeChargeDescription', { point })}
        buttons={
          <Button
            sx={{ minWidth: 212 }}
            variant="contained"
            color="primary"
            size="large"
            onClick={onClose}
          >
            OK
          </Button>
        }
      />
    )
  }

type CardRegisterModalProps = {
  open: boolean
  onClose: () => void
}
const CardRegisterModal: Organism<CardRegisterModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation('prepaidPointCharge')
  const { routerPages } = useRouter()
  return (
    <InformationModal
      open={open}
      onClose={onClose}
      title={t('CardRegisterModalTitle')}
      content={t('CardRegisterModalDescription')}
      buttons={
        <>
          <Button
            sx={{ minWidth: 212 }}
            variant="contained"
            color="primary"
            size="large"
            href={routerPages.PaymentDetail}
          >
            {t('confirmRegisterModal')}
          </Button>
          <Button
            sx={{ minWidth: 212 }}
            variant="text"
            color="primary"
            onClick={onClose}
          >
            {t('cancel')}
          </Button>
        </>
      }
    />
  )
}

const PointChargeCardSkeletonCell: Cell = () => (
  <Skeleton
    variant="rectangular"
    width="100%"
    height={56}
    sx={{ borderRadius: '2px' }}
  />
)
